<template>
  <button 
    class="cms-icon-button" 
    :class="[variant, size]"
    :disabled="disabled"
    :type="type"
    @click="$emit('click', $event)"
  >
    <v-icon :icon="icon" :size="iconSize" />
    <span v-if="tooltip" class="cms-icon-button-tooltip">{{ tooltip }}</span>
  </button>
</template>

<script lang="ts" setup>
import VIcon from '@/components/vendor/basic/icon/VIcon.vue';

interface Props {
  icon: string;
  variant?: 'default' | 'primary' | 'secondary' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  tooltip?: string;
}

withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'md',
  disabled: false,
  type: 'button'
});

const iconSize = 'sm';

defineEmits<{
  (e: 'click', event: MouseEvent): void;
}>();
</script>

<style lang="scss" scoped>
@import './cms-styles.scss';

.cms-icon-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: $cms-radius-sm;
  cursor: pointer;
  transition: all $cms-transition-base;
  background: transparent;
  color: $cms-text-primary;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  // Sizes
  &.sm {
    padding: $cms-spacing-xs;
    width: 28px;
    height: 28px;
  }
  
  &.md {
    padding: $cms-spacing-sm;
    width: 32px;
    height: 32px;
  }
  
  &.lg {
    padding: $cms-spacing-sm;
    width: 40px;
    height: 40px;
  }
  
  // Variants
  &.default {
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
    }
  }
  
  &.primary {
    color: $cms-primary-color;
    
    &:hover:not(:disabled) {
      background: $cms-primary-light;
    }
  }
  
  &.secondary {
    color: $cms-text-secondary;
    
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
      color: $cms-text-primary;
    }
  }
  
  &.danger {
    color: $cms-danger-color;
    
    &:hover:not(:disabled) {
      background: rgba(244, 67, 54, 0.1);
    }
  }
}

// Tooltip
.cms-icon-button-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%) translateY(-4px);
  padding: $cms-spacing-xs $cms-spacing-sm;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: $cms-font-size-xs;
  border-radius: $cms-radius-sm;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity $cms-transition-fast;
  z-index: 1000;
}

.cms-icon-button:hover .cms-icon-button-tooltip {
  opacity: 1;
}
</style>

